import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [settings, setSettings] = useState({
    emailNotifications: true,
    lowConcessionAlerts: true,
    manualBookingNotifications: true,
    concessionAdditionNotifications: true
  });
  const [loading, setLoading] = useState(false);

  const fetchNotificationSettings = useCallback(async () => {
    try {
      const response = await axios.get('/api/notifications/settings');
      setSettings(response.data.settings || {});
    } catch (error) {
      console.error('Error fetching notification settings:', error);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axios.get('/api/notifications');
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []);

  useEffect(() => {
    if (user?._id) {
      fetchNotificationSettings();
      fetchNotifications();
    }
  }, [user?._id, fetchNotificationSettings, fetchNotifications]);

  const updateNotificationSettings = async (newSettings) => {
    try {
      setLoading(true);
      const response = await axios.put('/api/notifications/settings', newSettings);

      if (response.status === 200) {
        setSettings(newSettings);
        return { success: true };
      } else {
        return { success: false, error: 'Failed to update settings' };
      }
    } catch (error) {
      console.error('Error updating notification settings:', error);
      return { success: false, error: 'Network error' };
    } finally {
      setLoading(false);
    }
  };

  const sendNotification = async (notificationData) => {
    try {
      const response = await axios.post('/api/notifications/send', notificationData);

      if (response.status === 200) {
        return { success: true };
      } else {
        return { success: false, error: 'Failed to send notification' };
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      const response = await axios.put(`/api/notifications/${notificationId}/read`);

      if (response.status === 200) {
        setNotifications(prev => 
          prev.map(notif => 
            notif._id === notificationId 
              ? { ...notif, isRead: true }
              : notif
          )
        );
        return { success: true };
      } else {
        return { success: false, error: 'Failed to mark notification as read' };
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const response = await axios.delete(`/api/notifications/${notificationId}`);

      if (response.status === 200) {
        setNotifications(prev => prev.filter(notif => notif._id !== notificationId));
        return { success: true };
      } else {
        return { success: false, error: 'Failed to delete notification' };
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const getUnreadCount = () => {
    return notifications.filter(notif => !notif.isRead).length;
  };

  const value = {
    notifications,
    settings,
    loading,
    updateNotificationSettings,
    sendNotification,
    markNotificationAsRead,
    deleteNotification,
    getUnreadCount,
    refreshNotifications: fetchNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};



